import { lazy, Suspense, useState, useEffect, useLayoutEffect, useCallback, useRef, type MutableRefObject } from "react";
import type { UIMessage } from "ai";
import type { MessageMetrics, TurnPhase } from "../hooks/useChat";
import type { SessionType, NewSessionOpts } from "./NewSessionDialog";
import { useKeepsakes } from "../hooks/useKeepsakes";
import { Sidebar } from "./Sidebar";
import { ChatArea } from "./ChatArea";
import { BrainStrip } from "./BrainStrip";
import { VoiceZone } from "./VoiceZone";
import { TranscriptPanel } from "./TranscriptPanel";
import { SettingsPanel } from "./SettingsPanel";
import { KeepsakesGallery } from "./KeepsakesGallery";
import { Menu, Download, LibraryBig, PanelLeftOpen } from "lucide-react";
import { AtomIcon } from "./AtomIcon";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useI18n } from "../i18n";
import type { ModelEntry, ChatConfig, EffortLevel } from "../shared/chat-config";
import type { Project, ProjectPatch, Session } from "../auth/types";
import type { PendingAttachment } from "./AttachmentPreview";
import type { AudioCache } from "../voice/audio-cache";
import type { LocalPreferences } from "../hooks/useSettings";
import type { VoiceState } from "../voice/types";
import { renderClientLayer } from "../layers";
import type { CompanionDescriptor } from "../brain/persona";
import type { WritingRoomDocumentUpdated } from "../layers/writing-room/WritingRoomLayer";
import type { RoomState } from "../layers/writing-room/room-state";

const LazyLibraryLayer = lazy(() =>
  import("../layers/library/LibraryLayer").then((module) => ({ default: module.LibraryLayer })),
);

interface AppShellProps {
  messages: UIMessage[];
  messageMetrics?: Map<string, MessageMetrics>;
  isLoading: boolean;
  connected: boolean;
  switching?: boolean;
  hasConnected?: boolean;
  error?: Error;
  sessionType: SessionType | null;
  onSend: (message: string, attachments?: PendingAttachment[]) => void;
  onStop: () => void;
  onClear?: () => void;
  onNewSession: (opts: NewSessionOpts) => void;
  // Session management
  sessions: Session[];
  archivedSessions: Session[];
  activeSessionId: string | null;
  onSwitchSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, label: string) => void;
  onTogglePin: (sessionId: string) => void;
  onArchiveSession: (sessionId: string) => void;
  onRestoreSession: (sessionId: string) => void;
  projects: Project[];
  activeProject?: Project | null;
  projectsLoading: boolean;
  projectsError: string | null;
  onCreateProject: (name: string, identity: string) => Promise<Project | null>;
  onRenameProject: (projectId: string, name: string) => Promise<boolean>;
  onAssignSessionProject: (sessionId: string, project: Project | null) => Promise<boolean>;
  /** WR-0 inline binding (Writer's Room unbound empty state) — applies the
   *  repository-binding response to the SAME central project store that
   *  `activeProject` derives from, so the room's prop refreshes through the
   *  normal render cycle rather than a parallel local state. */
  onProjectPatched: (patch: ProjectPatch) => void;
  pendingProjectMoveSessionIds?: ReadonlySet<string>;
  config: ChatConfig;
  currentModel: ModelEntry;
  availableModels: ModelEntry[];
  onModelChange: (modelId: string) => void;
  onEffortChange: (effort: EffortLevel) => void;
  onToggleThinking: () => void;
  voiceState?: VoiceState;
  getAudioLevel?: () => number;
  onSpeak?: (message: UIMessage) => void;
  speakingMessageId?: string | null;
  voiceMode?: boolean;
  onToggleVoiceMode?: () => void;
  onStopSpeaking?: () => void;
  seekTo?: (messageId: string, fraction: number) => void;
  getPlaybackProgress?: () => number;
  audioCache?: AudioCache;
  voiceId?: string;
  onRestartVoice?: () => void;
  playbackSpeed?: number;
  onSpeedChange?: (speed: number) => void;
  companionName?: string;
  /** PATH B SLICE 4 — server-sourced descriptor fields for the active companion. */
  companionSubtitle?: string;
  companionShortDesc?: string;
  /** PATH B SLICE 4 — full server companion allowlist (init frame), passed to
   *  NewSessionDialog for the picker. null/undefined = not yet connected. */
  companions?: CompanionDescriptor[] | null;
  prefs: LocalPreferences;
  updatePref: <K extends keyof LocalPreferences>(key: K, value: LocalPreferences[K]) => void;
  sendReasoningConfig?: (enabled: boolean) => void;
  showReasoning?: boolean;
  sttAvailable?: boolean;
  sendSTTAudio?: (blob: Blob, format?: string) => void;
  onSTTResultRef?: MutableRefObject<((text: string) => void) | null>;
  sttStreaming?: import("../hooks/useStreamingSTT").SttStreamingProps;
  activeAgents?: Array<{ name: string; task: string; startedAt: number }>;
  sendRawWs?: (payload: Record<string, unknown>) => void;
  writingRoomDocumentUpdatedRef?: MutableRefObject<((event: WritingRoomDocumentUpdated) => void) | null>;
  /** WR-2 (ADR-018) getter-pattern seam — threaded straight through to
   *  WritingRoomLayer's `roomStateRef` prop, no AppShell-side logic. */
  roomStateRef?: MutableRefObject<(() => RoomState | null) | null>;
  onReactionRef?: MutableRefObject<((messageId: string, emoji: string, user: string, action: 'add' | 'remove') => void) | null>;
  onReactionSyncRef?: MutableRefObject<((reactions: Array<{ message_id: string; emoji: string; user: string }>) => void) | null>;
  pendingReactionsRef?: MutableRefObject<Array<{ messageId: string; emoji: string; user: string; action: 'add' | 'remove' }>>;
  pendingReactionSyncsRef?: MutableRefObject<Array<Array<{ message_id: string; emoji: string; user: string }>>>;
  isReplaying?: boolean;
  turnPhase?: TurnPhase;
  turnStartedAt?: number | null;
  subprocessAlive?: boolean;
}

/** Desktop-only (>=769px) sidebar collapse — a preference, not a surface.
 *  Persisted like the Writer's Room's binder-collapse (same shell-scope
 *  discipline: try/catch best-effort, validated read) so a reload keeps
 *  whatever a person last chose. Never read below 769px: that width already
 *  has its own slide-over sidebar (`.sidebar.open`) with its own state
 *  (`sidebarOpen`), untouched by this flag. */
const SIDEBAR_COLLAPSED_STORAGE_KEY = "muse:shell:sidebar-collapsed";

function readStoredSidebarCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function AppShell({
  messages,
  messageMetrics = new Map(),
  isLoading,
  connected,
  switching,
  hasConnected,
  error,
  sessionType,
  onSend,
  onStop,
  onClear,
  onNewSession,
  sessions,
  archivedSessions,
  activeSessionId,
  onSwitchSession,
  onDeleteSession,
  onRenameSession,
  onTogglePin,
  onArchiveSession,
  onRestoreSession,
  projects,
  activeProject = null,
  projectsLoading,
  projectsError,
  onCreateProject,
  onRenameProject,
  onAssignSessionProject,
  onProjectPatched,
  pendingProjectMoveSessionIds = new Set(),
  config,
  currentModel,
  availableModels,
  onModelChange,
  onEffortChange,
  onToggleThinking,
  voiceState,
  getAudioLevel,
  onSpeak,
  speakingMessageId,
  voiceMode,
  onToggleVoiceMode,
  onStopSpeaking,
  seekTo,
  getPlaybackProgress,
  audioCache,
  voiceId,
  onRestartVoice,
  playbackSpeed,
  onSpeedChange,
  companionName = "Rook",
  companionSubtitle,
  companionShortDesc,
  companions,
  prefs,
  updatePref,
  sendReasoningConfig,
  showReasoning,
  sttAvailable,
  sendSTTAudio,
  onSTTResultRef,
  sttStreaming,
  activeAgents = [],
  sendRawWs,
  writingRoomDocumentUpdatedRef,
  roomStateRef,
  onReactionRef,
  onReactionSyncRef,
  pendingReactionsRef,
  pendingReactionSyncsRef,
  isReplaying = false,
  turnPhase,
  turnStartedAt,
  subprocessAlive = true,
}: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const { canInstall, install } = useInstallPrompt();
  const [scrollToMessageId, setScrollToMessageId] = useState<string | null>(null);
  const { t } = useI18n();

  // WR-W S1 — desktop sidebar collapse. `sidebarOpen` above stays the mobile
  // slide-over's own state; this is a SEPARATE, desktop-only preference that
  // hides `.sidebar` via CSS (app.css `[data-sidebar-collapsed]`).
  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(readStoredSidebarCollapsed);
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsedState((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      } catch {
        // Best-effort persistence only — an unavailable/full localStorage
        // must never block the layout change itself.
      }
      return next;
    });
  }, []);
  // Set immediately before an AUTO (non-user-initiated) collapse so the
  // focus-rescue effect below can tell the two apart and skip its own rescue
  // for this one — see collapseSidebarForDocumentView's own doc comment.
  const skipCollapseFocusRescueRef = useRef(false);
  // Writer's Room document view's own one-way auto-collapse (Falco has
  // twice noted the sidebar eating his screen there). Deliberately NOT a
  // toggle and NOT paired with any "restore on leave" — Eli's original
  // guidance against document view auto-collapsing the shell sidebar was
  // specifically about save/restore-prior-state across the shell/layer
  // boundary during a book crunch (a bug farm: what if the writer manually
  // toggled it WHILE in document view, what if two tabs disagree about
  // "the" prior state, what does "restore" even mean after a reload). A
  // ONE-WAY force avoids that whole class of bug structurally: there is no
  // prior state captured, therefore nothing to get wrong restoring it.
  // Idempotent (forcing an already-collapsed sidebar collapsed again is a
  // no-op — the functional updater's `prev` check below skips both the
  // localStorage write and the state change entirely), and reuses the
  // EXACT SAME persisted preference Cmd+\/the header toggle already own —
  // not a second parallel "document view sidebar" state to keep in sync.
  // The one thing that must not happen: stealing focus off whatever the
  // writer was doing (usually the editor, or the Document view-toggle
  // button they just clicked) the instant this fires — see the guard below.
  const collapseSidebarForDocumentView = useCallback(() => {
    setSidebarCollapsedState((prev) => {
      if (prev) return prev; // already collapsed — no write, no re-render
      skipCollapseFocusRescueRef.current = true;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, "true");
      } catch {
        // Best-effort persistence only, same discipline as toggleSidebarCollapsed.
      }
      return true;
    });
  }, []);
  // Read outside React's render cycle by the Cmd+\ keydown handler below —
  // same same-tick-ref rationale as the Writer's Room's mobile-layout ref.
  const { matches: isDesktopWidth, matchesRef: isDesktopWidthRef } = useMediaQuery("(min-width: 769px)");

  // Focus rescue for the collapse transition (S1 §9): collapse moves focus to
  // the re-open rail, expand moves it to the header toggle. Keyed on the
  // TRANSITION via a wasCollapsed guard — without it, mount (where prev ===
  // initial) would fire the same branch and steal focus from whatever the
  // page loaded with. The `skipCollapseFocusRescueRef` branch below is the
  // SAME guard for a different cause: a collapse triggered by
  // collapseSidebarForDocumentView (Writer's Room entering document view)
  // is not a deliberate "I want to focus the reopen affordance" action the
  // way clicking the header toggle or Cmd+\ is — rescuing focus there would
  // yank it away from the editor the writer just navigated into.
  const sidebarRailRef = useRef<HTMLButtonElement>(null);
  const sidebarCollapseToggleRef = useRef<HTMLButtonElement>(null);
  const wasSidebarCollapsedRef = useRef(sidebarCollapsed);
  useLayoutEffect(() => {
    if (wasSidebarCollapsedRef.current === sidebarCollapsed) return;
    wasSidebarCollapsedRef.current = sidebarCollapsed;
    if (skipCollapseFocusRescueRef.current) {
      skipCollapseFocusRescueRef.current = false;
      return;
    }
    if (sidebarCollapsed) {
      sidebarRailRef.current?.focus();
    } else {
      sidebarCollapseToggleRef.current?.focus();
    }
  }, [sidebarCollapsed]);

  // The active session's OWN companion identity (not companionName, which is
  // display text) — required so useKeepsakes can send the correct
  // ?companion= param. See useKeepsakes.ts's identity param doc for why: a
  // missing/wrong identity here reintroduces "keepsake button blocked in
  // Rainer chats" (the request silently defaults to 'rook' server-side and
  // 404s against a rainer-owned session).
  const activeSessionIdentity = sessions.find((s) => s.id === activeSessionId)?.identity;

  // Writer's Room in-room project attach (task #9). Built here, once, so the
  // room itself never learns session-id semantics — it only ever sees "here
  // is a project, attach it" and a boolean result.
  const onAttachProject = useCallback(async (project: Project): Promise<boolean> => {
    if (!activeSessionId) return false;
    return onAssignSessionProject(activeSessionId, project);
  }, [activeSessionId, onAssignSessionProject]);

  // Single source of truth for keepsakes — shared between ChatArea and Sidebar
  const { isKeepsaked, toggleKeepsake, allKeepsakes, fetchAllKeepsakes } =
    useKeepsakes(activeSessionId, activeSessionIdentity);

  const [keepsakesGalleryOpen, setKeepsakesGalleryOpen] = useState(false);

  // Focus management for voice mode transitions
  const voiceModeFirstFocusRef = useRef<HTMLButtonElement>(null);
  // Saves the element that was focused before entering voice mode so we can restore it on exit
  const voiceModePrevFocusRef = useRef<HTMLElement | null>(null);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const clearScrollTarget = useCallback(() => setScrollToMessageId(null), []);

  // Poll cross-session keepsakes every 30s so the sidebar stays current
  useEffect(() => {
    fetchAllKeepsakes();
    const interval = setInterval(fetchAllKeepsakes, 30_000);
    return () => clearInterval(interval);
  }, [fetchAllKeepsakes]);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);
  const closeLibrary = useCallback(() => setLibraryOpen(false), []);
  const openLibrary = useCallback(() => {
    setSidebarOpen(false);
    setSettingsOpen(false);
    setKeepsakesGalleryOpen(false);
    setLibraryOpen(true);
  }, []);

  // Close sidebar before opening settings — both use full-width overlays on mobile
  const openSettings = useCallback(() => {
    setSidebarOpen(false);
    setSettingsOpen(true);
  }, []);

  const closeKeepsakesGallery = useCallback(() => setKeepsakesGalleryOpen(false), []);

  // Same overlay-exclusivity convention as openSettings.
  const openKeepsakesGallery = useCallback(() => {
    setSidebarOpen(false);
    setKeepsakesGalleryOpen(true);
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const meta = e.metaKey || e.ctrlKey;
      // Escape — close the topmost surface first.
      if (e.key === "Escape") {
        if (libraryOpen) { setLibraryOpen(false); return; }
        if (settingsOpen) { setSettingsOpen(false); return; }
        if (keepsakesGalleryOpen) { setKeepsakesGalleryOpen(false); return; }
        if (sidebarOpen) { closeSidebar(); return; }
        return;
      }
      // Skip shortcuts when typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      // Cmd+Shift+N — new personal session
      if (meta && e.shiftKey && e.key === "N") {
        e.preventDefault();
        closeSidebar();
        onNewSession({ type: "personal" as SessionType });
        return;
      }
      // Cmd+\ — toggle sidebar. Width decides WHICH one: >=769px is the
      // desktop collapse preference this slice makes honest; <769px stays
      // the mobile slide-over it always was. Collapse is a preference, not a
      // surface — it's deliberately absent from the Escape ladder above.
      if (meta && e.key === "\\") {
        e.preventDefault();
        if (isDesktopWidthRef.current) {
          toggleSidebarCollapsed();
        } else {
          setSidebarOpen(prev => !prev);
        }
        return;
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sidebarOpen, settingsOpen, keepsakesGalleryOpen, libraryOpen, closeSidebar, onNewSession, isDesktopWidthRef, toggleSidebarCollapsed]);

  // Move focus when voice mode toggles
  useEffect(() => {
    if (voiceMode) {
      // Save the element that triggered the mode switch before moving focus
      voiceModePrevFocusRef.current = document.activeElement as HTMLElement;
      // Entering voice mode — focus the first interactive control in VoiceZone
      voiceModeFirstFocusRef.current?.focus();
    } else {
      // Exiting voice mode — restore focus to where it was before entering
      voiceModePrevFocusRef.current?.focus();
      voiceModePrevFocusRef.current = null;
    }
  }, [voiceMode]);

  const toggleTranscript = useCallback(() => setTranscriptOpen(o => !o), []);

  function handleNewSession(opts: NewSessionOpts) {
    closeSidebar();
    onNewSession(opts);
  }

  // Shared by the Sidebar's mini keepsakes panel AND the full KeepsakesGallery
  // — jumping to a keepsake's source message from either surface does the
  // same three things: switch session if needed, arm the scroll target, and
  // close whichever overlay triggered the jump.
  const handleKeepsakeSelect = useCallback(
    (messageId: string, sessionId: string) => {
      if (sessionId !== activeSessionId) onSwitchSession(sessionId);
      setScrollToMessageId(messageId);
      closeSidebar();
      setKeepsakesGalleryOpen(false);
    },
    [activeSessionId, onSwitchSession, closeSidebar],
  );

  // Derive connection status for VoiceZone and ChatArea
  const connectionStatus = switching
    ? "switching" as const
    : connected
      ? "connected" as const
      : hasConnected
        ? "reconnecting" as const
        : "connecting" as const;

  // WR-M: the Writer's Room owns the entire phone screen — its own top bar
  // carries the menu, the status and the surface flip, so the shell's header
  // and install banner are hidden underneath it (app.css, <=700px). Hoisted
  // rather than inlined because the same condition gates the layer render
  // below; two copies of it would drift.
  const isWritingLayer = sessionType === "writing" && !voiceMode;

  return (
    <div
      className="app-shell"
      data-layer={isWritingLayer ? "writing" : undefined}
      data-sidebar-collapsed={sidebarCollapsed || undefined}
    >
      {/* Mobile header with hamburger */}
      <div className="mobile-header">
        <button
          className="btn btn-icon btn-ghost"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        <span className="mobile-header__brand">
          <AtomIcon size={20} />
          <span className="display" style={{ fontSize: "1rem" }}>SOVEREIGN MUSE</span>
        </span>
        <button className="btn btn-icon btn-ghost mobile-header__library" onClick={openLibrary} aria-label="Open Library">
          <LibraryBig size={19} />
        </button>
      </div>

      {canInstall && (
        <button className="install-banner" onClick={install}>
          <Download size={16} />
          <span>Install MUSE App</span>
        </button>
      )}

      {/* Sidebar overlay for mobile */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? "visible" : ""}`}
        onClick={closeSidebar}
      />

      <Sidebar
        onNewSession={handleNewSession}
        sessions={sessions}
        archivedSessions={archivedSessions}
        activeSessionId={activeSessionId}
        onSwitchSession={onSwitchSession}
        onDeleteSession={onDeleteSession}
        onRenameSession={onRenameSession}
        onTogglePin={onTogglePin}
        onArchiveSession={onArchiveSession}
        onRestoreSession={onRestoreSession}
        projects={projects}
        projectsLoading={projectsLoading}
        projectsError={projectsError}
        onCreateProject={onCreateProject}
        onRenameProject={onRenameProject}
        onAssignSessionProject={onAssignSessionProject}
        pendingProjectMoveSessionIds={pendingProjectMoveSessionIds}
        // MUSE GUIDE: KEEPSAKE NAVIGATION — handleKeepsakeSelect switches session then hands scrollToMessageId to MessageList's retry-scroll effect.
        onKeepsakeSelect={handleKeepsakeSelect}
        onOpenKeepsakesGallery={openKeepsakesGallery}
        onOpenLibrary={openLibrary}
        allKeepsakes={allKeepsakes}
        className={sidebarOpen ? "open" : ""}
        companions={companions}
        collapsed={sidebarCollapsed}
        /* Width-gated like the rail: below 769px the collapse preference has
           no CSS effect, so exposing the toggle there flips aria-expanded on
           a control whose target is fully visible — ARIA decoupled from
           reality (Fischer, S1 review). Sidebar renders no trailing button
           when the callback is absent. */
        onToggleCollapsed={isDesktopWidth ? toggleSidebarCollapsed : undefined}
        collapseToggleRef={sidebarCollapseToggleRef}
      />

      {/* Re-open rail — JS-gated on collapsed AND desktop width, not CSS:
          below 769px the mobile hamburger already owns "open the sidebar",
          and a CSS-only duplicate would leave two elements with the same
          accessible target (#app-sidebar) in the tree at once. */}
      {sidebarCollapsed && isDesktopWidth && (
        <button
          ref={sidebarRailRef}
          type="button"
          className="sidebar-rail"
          aria-label={t("sidebar.expand")}
          aria-expanded={false}
          aria-controls="app-sidebar"
          onClick={toggleSidebarCollapsed}
        >
          <PanelLeftOpen size={16} />
        </button>
      )}

      <KeepsakesGallery
        open={keepsakesGalleryOpen}
        onClose={closeKeepsakesGallery}
        keepsakes={allKeepsakes}
        companions={companions}
        onSelect={handleKeepsakeSelect}
      />

      <div className="main-content">
        {sessionType === "storyboard-studio" && !voiceMode ? (
          renderClientLayer("storyboard-studio")
        ) : (sessionType === "film-studio" || sessionType === "film-editing") && !voiceMode ? (
          renderClientLayer("film-studio", {
            chatArea: (
              <ChatArea
                messages={messages}
                messageMetrics={messageMetrics}
                isLoading={isLoading}
                connected={connected}
                switching={switching}
                hasConnected={hasConnected}
                error={error}
                sessionType={sessionType}
                onSend={onSend}
                onStop={onStop}
                companionName={companionName}
                onSettingsOpen={openSettings}
                activeSessionId={activeSessionId}
                config={config}
                currentModel={currentModel}
                availableModels={availableModels}
                onModelChange={onModelChange}
                onEffortChange={onEffortChange}
                onToggleThinking={onToggleThinking}
                onSpeak={onSpeak}
                speakingMessageId={speakingMessageId}
                voiceMode={voiceMode}
                onToggleVoiceMode={onToggleVoiceMode}
                voiceState={voiceState}
                onStopSpeaking={onStopSpeaking}
                seekTo={seekTo}
                getPlaybackProgress={getPlaybackProgress}
                audioCache={audioCache}
                voiceId={voiceId}
                playbackSpeed={playbackSpeed}
                onSpeedChange={onSpeedChange}
                isKeepsaked={isKeepsaked}
                onKeepsake={toggleKeepsake}
                scrollToMessageId={scrollToMessageId}
                onScrollToComplete={clearScrollTarget}
                sttAvailable={sttAvailable}
                sendSTTAudio={sendSTTAudio}
                onSTTResultRef={onSTTResultRef}
                sttStreaming={sttStreaming}
                activeAgents={activeAgents}
                sendRawWs={sendRawWs}
                onReactionRef={onReactionRef}
                onReactionSyncRef={onReactionSyncRef}
                pendingReactionsRef={pendingReactionsRef}
                pendingReactionSyncsRef={pendingReactionSyncsRef}
                isReplaying={isReplaying}
                showReasoning={showReasoning}
                turnPhase={turnPhase}
                turnStartedAt={turnStartedAt}
                subprocessAlive={subprocessAlive}
              />
            ),
          })
        ) : isWritingLayer ? (
          renderClientLayer("writing", {
            project: activeProject,
            onProjectPatched,
            onOpenAppMenu: () => setSidebarOpen(true),
            onEnterDocumentView: collapseSidebarForDocumentView,
            connected,
            sendRawWs,
            documentUpdatedRef: writingRoomDocumentUpdatedRef,
            roomStateRef,
            projects,
            activeSessionId,
            creatorIdentity: activeSessionIdentity ?? null,
            onAttachProject,
            onCreateProject,
            chatArea: (
              <ChatArea
                messages={messages}
                messageMetrics={messageMetrics}
                isLoading={isLoading}
                connected={connected}
                switching={switching}
                hasConnected={hasConnected}
                error={error}
                sessionType={sessionType}
                onSend={onSend}
                onStop={onStop}
                companionName={companionName}
                onSettingsOpen={openSettings}
                activeSessionId={activeSessionId}
                config={config}
                currentModel={currentModel}
                availableModels={availableModels}
                onModelChange={onModelChange}
                onEffortChange={onEffortChange}
                onToggleThinking={onToggleThinking}
                onSpeak={onSpeak}
                speakingMessageId={speakingMessageId}
                voiceMode={voiceMode}
                onToggleVoiceMode={onToggleVoiceMode}
                voiceState={voiceState}
                onStopSpeaking={onStopSpeaking}
                seekTo={seekTo}
                getPlaybackProgress={getPlaybackProgress}
                audioCache={audioCache}
                voiceId={voiceId}
                playbackSpeed={playbackSpeed}
                onSpeedChange={onSpeedChange}
                isKeepsaked={isKeepsaked}
                onKeepsake={toggleKeepsake}
                scrollToMessageId={scrollToMessageId}
                onScrollToComplete={clearScrollTarget}
                sttAvailable={sttAvailable}
                sendSTTAudio={sendSTTAudio}
                onSTTResultRef={onSTTResultRef}
                sttStreaming={sttStreaming}
                activeAgents={activeAgents}
                sendRawWs={sendRawWs}
                onReactionRef={onReactionRef}
                onReactionSyncRef={onReactionSyncRef}
                pendingReactionsRef={pendingReactionsRef}
                pendingReactionSyncsRef={pendingReactionSyncsRef}
                isReplaying={isReplaying}
                showReasoning={showReasoning}
                turnPhase={turnPhase}
                turnStartedAt={turnStartedAt}
                subprocessAlive={subprocessAlive}
              />
            ),
          })
        ) : voiceMode ? (
          <VoiceZone
            voiceState={voiceState ?? "unloaded"}
            getAudioLevel={getAudioLevel ?? (() => 0)}
            companionName={companionName}
            connectionStatus={connectionStatus}
            transcriptOpen={transcriptOpen}
            onToggleTranscript={toggleTranscript}
            onSettingsOpen={openSettings}
            onExitVoiceMode={onToggleVoiceMode ?? (() => {})}
            connected={connected}
            modelLabel={currentModel.label}
            constellationTouch={prefs.constellationTouch}
            firstFocusRef={voiceModeFirstFocusRef}
            sttAvailable={sttAvailable}
            sendSTTAudio={sendSTTAudio}
            onSTTResultRef={onSTTResultRef}
            onSend={onSend}
          />
        ) : (
          <>
            <ChatArea
              messages={messages}
              messageMetrics={messageMetrics}
              isLoading={isLoading}
              connected={connected}
              switching={switching}
              hasConnected={hasConnected}
              error={error}
              sessionType={sessionType}
              onSend={onSend}
              onStop={onStop}
              companionName={companionName}
              onSettingsOpen={openSettings}
              activeSessionId={activeSessionId}
              config={config}
              currentModel={currentModel}
              availableModels={availableModels}
              onModelChange={onModelChange}
              onEffortChange={onEffortChange}
              onToggleThinking={onToggleThinking}
              onSpeak={onSpeak}
              speakingMessageId={speakingMessageId}
              voiceMode={voiceMode}
              onToggleVoiceMode={onToggleVoiceMode}
              voiceState={voiceState}
              onStopSpeaking={onStopSpeaking}
              seekTo={seekTo}
              getPlaybackProgress={getPlaybackProgress}
              audioCache={audioCache}
              voiceId={voiceId}
              playbackSpeed={playbackSpeed}
              onSpeedChange={onSpeedChange}
              isKeepsaked={isKeepsaked}
              onKeepsake={toggleKeepsake}
              scrollToMessageId={scrollToMessageId}
              onScrollToComplete={clearScrollTarget}
              sttAvailable={sttAvailable}
              sendSTTAudio={sendSTTAudio}
              onSTTResultRef={onSTTResultRef}
              sttStreaming={sttStreaming}
              activeAgents={activeAgents}
              sendRawWs={sendRawWs}
              onReactionRef={onReactionRef}
              onReactionSyncRef={onReactionSyncRef}
              pendingReactionsRef={pendingReactionsRef}
              pendingReactionSyncsRef={pendingReactionSyncsRef}
              isReplaying={isReplaying}
              showReasoning={showReasoning}
              turnPhase={turnPhase}
              turnStartedAt={turnStartedAt}
              subprocessAlive={subprocessAlive}
            />
            <BrainStrip connected={connected} modelLabel={currentModel.label} companionName={companionName} />
          </>
        )}
      </div>

      {/* Transcript panel — only in voice mode */}
      {voiceMode && (
        <TranscriptPanel
          open={transcriptOpen}
          onClose={toggleTranscript}
          messages={messages}
          isLoading={isLoading}
          error={error}
          connected={connected}
          onSend={onSend}
          onStop={onStop}
          onStopSpeaking={onStopSpeaking}
          companionName={companionName}
          activeSessionId={activeSessionId}
          config={config}
          currentModel={currentModel}
          availableModels={availableModels}
          onModelChange={onModelChange}
          onEffortChange={onEffortChange}
          onToggleThinking={onToggleThinking}
          onSpeak={onSpeak}
          speakingMessageId={speakingMessageId}
          seekTo={seekTo}
          getPlaybackProgress={getPlaybackProgress}
          audioCache={audioCache}
          voiceId={voiceId}
          playbackSpeed={playbackSpeed}
          onSpeedChange={onSpeedChange}
        />
      )}

      {libraryOpen && (
        <Suspense fallback={<div className="library-shell-loading">Opening Library…</div>}>
          <LazyLibraryLayer onClose={closeLibrary} />
        </Suspense>
      )}

      <SettingsPanel
        open={settingsOpen}
        onClose={closeSettings}
        onRestartVoice={onRestartVoice}
        prefs={prefs}
        updatePref={updatePref}
        sendReasoningConfig={sendReasoningConfig}
        showReasoning={showReasoning}
        availableModels={availableModels}
        currentModel={currentModel}
        onModelChange={onModelChange}
        companionName={companionName}
        companionSubtitle={companionSubtitle}
        companionShortDesc={companionShortDesc}
      />
    </div>
  );
}
